/* eslint-disable */

import {
  DayHeatingSchedules,
  HeatingSchedulesByDayResponse,
  HeatingSchedulesResponse,
  HeatingSetpointInputs,
  HotWaterSchedulesResponse,
} from '../../../shared/api-clients/gql';
import { Day } from '../contexts/heatingSchedules';
import { UIConfig } from './UIConfig';

export const mergeUiSchedules = (
  schedules: DayHeatingSchedules[]
): HeatingSetpointInputs[] => {
  const res = schedules
    ?.flatMap((schedule: DayHeatingSchedules) => {
      const startSchedule: HeatingSetpointInputs = {
        time: schedule.start,
        type: 'start',
        temperature: schedule.temperature,
      };
      const endSchedule: HeatingSetpointInputs = {
        time: schedule.end,
        type: 'end',
        temperature: UIConfig.defaultSetpoint,
      };

      return [startSchedule, endSchedule];
    })
    .sort((a, b) => a.time.localeCompare(b.time));
  return res;
};

const sorter = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

function sortByDay(day1, day2) {
  return sorter[day1] - sorter[day2];
}

export interface IScheduleArray extends DayHeatingSchedules {
  day: Day[] | string[];
  start: string;
  user_time?: string;
  end?: string;
  temperature?: number;
}

export interface IZoneArray {
  [key: string]: IScheduleArray[];
}
/**
 * Splits the schedule results into a zones with each zone having an array of schedules with a day array
 * @param scheduleResults Schedule results
 */
export function generateZoneSchedules(
  scheduleResults: HeatingSchedulesResponse
): {
  zones: IZoneArray;
} {
  // generate object key by zone_id
  // generate key via start:end:temp
  // push each day to that array of days
  const results = {};
  for (const dayKey in scheduleResults) {
    const daySchedules: HeatingSchedulesByDayResponse[] =
      scheduleResults[dayKey];
    if (!daySchedules) continue;

    for (const daySchedule of daySchedules) {
      const { zone_id, schedules } = daySchedule;

      if (!results[zone_id]) {
        results[zone_id] = {};
      }

      for (const schedule of schedules) {
        const key = `${schedule.start}-${schedule.end}-${schedule.temperature}`;
        if (!results[zone_id][key]) {
          results[zone_id][key] = { ...schedule, day: [dayKey] };
        } else {
          results[zone_id][key].day.push(dayKey);
        }
      }
    }
  }
  /** Flatten back to arrays */
  for (const zone in results) {
    const obj = results[zone];
    const array = [];
    for (const key in obj) {
      // reassign object to an array and sort the days of the week
      array.push({ ...obj[key], day: obj[key].day.sort(sortByDay) });
    }
    results[zone] = array;
  }
  return { zones: results };
}

export function generateHotWaterSchedules(
  scheduleResults: HotWaterSchedulesResponse
): {
  setpoints: IZoneArray;
} {
  // Flatten all schedule days into IScheduleArray[]
  const unmergedSchedules: IScheduleArray[] = [];
  for (const dayKey in scheduleResults) {
    const daySchedules = scheduleResults?.[
      dayKey as keyof HotWaterSchedulesResponse
    ]?.reduce<IScheduleArray[]>((acc, schedule) => {
      schedule.setpoints
        .filter((s) => !!s.user_time)
        .forEach((s) => acc.push({ day: [dayKey], start: s.user_time }));
      return acc;
    }, []);
    unmergedSchedules.push(...daySchedules);
  }

  // Merge schedules that have the same start time by adding to the day array
  const mergedSchedules = unmergedSchedules.reduce<{
    [startTime: string]: IScheduleArray;
  }>((acc, schedule) => {
    if (!acc[schedule.start]) {
      acc[schedule.start] = schedule;
    } else {
      const days = new Set([...acc[schedule.start].day, ...schedule.day]);
      acc[schedule.start].day = Array.from(days);
    }
    return acc;
  }, {});

  return { setpoints: { hot_water: Object.values(mergedSchedules) } };
}

export interface IActiveZones {
  [key: string]: { index: number; temperature: string };
}
function compareDays(array1: string[] | Day[], array2: string[] | Day[]) {
  const sArray1 = array1.sort(sortByDay);
  const sArray2 = array2.sort(sortByDay);
  return JSON.stringify(sArray1) === JSON.stringify(sArray2);
}

/**generate active zones */
export function generateActiveZones(
  zoneSchedules: IZoneArray,
  startTime: string,
  endTime?: string,
  days?: string[]
): IActiveZones {
  const activeZones = {};
  if (startTime && endTime) {
    for (const key in zoneSchedules) {
      let index = 0;
      const obj = zoneSchedules[key];
      for (const item of obj) {
        if (
          item.start === startTime &&
          item.end === endTime &&
          compareDays(item.day, days)
        ) {
          activeZones[key] = { temperature: item.temperature, index };
        }
        index++;
      }
    }
  }

  return activeZones;
}
